'use client';

import React, { useEffect, useState } from 'react';
import { Settings, Clock, AlertTriangle, CheckCircle2 } from 'lucide-react';
import { Appointment, Staff } from './types';

interface BookingsAgendaViewProps {
  staffList: Staff[];
  appointments: Appointment[];
  selectedDate: string;
  onSelectAppointment: (appointment: Appointment) => void;
  onSlotClick: (staffId: string, time: string) => void;
}

export const BookingsAgendaView: React.FC<BookingsAgendaViewProps> = ({
  staffList,
  appointments,
  selectedDate,
  onSelectAppointment,
  onSlotClick,
}) => {
  // Horas del día de 09:00 a 20:00 en intervalos de 30 min
  const timeSlots: string[] = [];
  for (let h = 9; h <= 20; h++) {
    timeSlots.push(`${h.toString().padStart(2, '0')}:00`);
    timeSlots.push(`${h.toString().padStart(2, '0')}:30`);
  }

  // Altura por bloque de 30 min en píxeles
  const slotHeightPx = 54;

  // Cálculo de posición en minutos desde las 09:00
  const getMinutesFromStart = (timeStr: string) => {
    const [h, m] = timeStr.split(':').map(Number);
    return (h - 9) * 60 + m;
  };

  // Posición de la línea roja de hora actual
  const [currentLineTop, setCurrentLineTop] = useState<number | null>(null);

  useEffect(() => {
    const updateTimeLine = () => {
      const now = new Date();
      const h = now.getHours();
      const m = now.getMinutes();
      if (h >= 9 && h <= 20) {
        const mins = (h - 9) * 60 + m;
        const top = (mins / 30) * slotHeightPx;
        setCurrentLineTop(top);
      } else {
        setCurrentLineTop(null);
      }
    };

    updateTimeLine();
    const interval = setInterval(updateTimeLine, 60000);
    return () => clearInterval(interval);
  }, [slotHeightPx]);

  // Si no hay staff cargado, mostrar fallback con Liliana y Juan Rojas
  const displayStaff =
    staffList.length > 0
      ? staffList
      : [
          { id: 'staff_liliana', name: 'Liliana', colorHex: '#c026d3' },
          { id: 'staff_juan', name: 'Juan Rojas', colorHex: '#10b981' },
        ];

  return (
    <div className="bg-white rounded-2xl border border-slate-200/90 shadow-xs mx-4 sm:mx-6 overflow-hidden mb-12">
      {/* Cabecera de Columnas: Engranaje a la izquierda + Columnas de Staff */}
      <div className="grid border-b border-slate-200/90 bg-slate-50/80 sticky top-0 z-20"
        style={{
          gridTemplateColumns: `80px repeat(${displayStaff.length}, minmax(260px, 1fr))`,
        }}
      >
        {/* Columna de Engranaje */}
        <div className="h-12 border-r border-slate-200/80 flex items-center justify-center text-slate-400">
          <Settings className="w-4 h-4" />
        </div>

        {/* Columnas de los Profesionales */}
        {displayStaff.map((st) => (
          <div
            key={st.id}
            className="h-12 border-r last:border-r-0 border-slate-200/80 px-4 flex items-center justify-center gap-2"
          >
            <div
              className="w-3 h-3 rounded-full"
              style={{ backgroundColor: st.colorHex || '#10b981' }}
            />
            <span className="font-extrabold text-xs sm:text-sm text-[#0f172a]">
              {st.name}
            </span>
          </div>
        ))}
      </div>

      {/* Cuerpo de la Grilla Horaria */}
      <div
        className="relative grid"
        style={{
          gridTemplateColumns: `80px repeat(${displayStaff.length}, minmax(260px, 1fr))`,
        }}
      >
        {/* Columna Izquierda: Etiquetas Horarias */}
        <div className="border-r border-slate-200/80 bg-slate-50/40 select-none">
          {timeSlots.map((time) => (
            <div
              key={time}
              style={{ height: `${slotHeightPx}px` }}
              className="border-b border-slate-200/50 flex items-start justify-end pr-3 pt-1 text-[11px] font-bold text-slate-400"
            >
              {time}
            </div>
          ))}
        </div>

        {/* Columnas de cada Profesional */}
        {displayStaff.map((st) => {
          const staffAppointments = appointments.filter(
            (a) => a.staffId === st.id && a.dateStr === selectedDate
          );

          return (
            <div
              key={st.id}
              className="border-r last:border-r-0 border-slate-200/80 relative bg-white"
            >
              {/* Líneas horizontales de 30 min */}
              {timeSlots.map((time) => (
                <div
                  key={time}
                  onClick={() => onSlotClick(st.id, time)}
                  style={{ height: `${slotHeightPx}px` }}
                  className="border-b border-slate-100 hover:bg-slate-50/70 transition cursor-pointer"
                />
              ))}

              {/* Turnos renderizados de este staff */}
              {staffAppointments.map((apt) => {
                const startMins = getMinutesFromStart(apt.startTime);
                if (startMins < 0) return null;

                const top = (startMins / 30) * slotHeightPx;
                const height = Math.max(
                  ((apt.duration || 45) / 30) * slotHeightPx - 4,
                  36
                );

                const isCompleted = apt.status.toLowerCase() === 'completed';
                const isOverturn = apt.isOverturn;

                return (
                  <div
                    key={apt.id}
                    onClick={(e) => {
                      e.stopPropagation();
                      onSelectAppointment(apt);
                    }}
                    style={{
                      top: `${top + 2}px`,
                      height: `${height}px`,
                      borderLeftColor: st.colorHex || '#10b981',
                    }}
                    className={`absolute left-2 right-2 rounded-xl p-2.5 border-l-4 shadow-sm cursor-pointer transition transform hover:scale-[1.01] flex flex-col justify-between overflow-hidden z-10 ${
                      isCompleted
                        ? 'bg-slate-100 border-slate-400 text-slate-600'
                        : isOverturn
                        ? 'bg-amber-50/90 border-amber-500 text-amber-950'
                        : 'bg-emerald-50/90 border-[#10b981] text-emerald-950'
                    }`}
                  >
                    <div className="flex items-start justify-between gap-1">
                      <div className="min-w-0">
                        <p className="font-extrabold text-xs truncate">
                          {apt.clientName}
                        </p>
                        <p className="text-[10px] opacity-80 truncate">
                          {apt.serviceName}
                        </p>
                      </div>

                      <span className="text-[10px] font-black shrink-0">
                        ${' '}
                        {apt.price?.toLocaleString('es-AR', {
                          minimumFractionDigits: 0,
                        })}
                      </span>
                    </div>

                    <div className="flex items-center justify-between text-[9px] font-bold pt-1 opacity-80">
                      <span>
                        {apt.startTime} - {apt.endTime}
                      </span>
                      {isOverturn && (
                        <span className="bg-amber-200/80 text-amber-800 px-1.5 py-0.5 rounded text-[8px] font-black uppercase">
                          Sobreturno
                        </span>
                      )}
                    </div>
                  </div>
                );
              })}
            </div>
          );
        })}

        {/* Línea Roja Dinámica de Hora Actual (AppGenda Fase 3) */}
        {currentLineTop !== null && (
          <div
            style={{ top: `${currentLineTop}px` }}
            className="absolute left-0 right-0 z-30 pointer-events-none flex items-center"
          >
            <div className="w-2.5 h-2.5 rounded-full bg-rose-600 -ml-1.5 shadow-sm" />
            <div className="w-full h-[2px] bg-rose-600" />
          </div>
        )}
      </div>
    </div>
  );
};
